import React, { useContext, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { uploadDocumentToPresignedS3 } from '@App/api/routes/util'
import { formatBytes } from '@App/helpers/format-bytes'
import { StoresContext } from '@App/contexts'
import { Maybe } from '@Types'
import { DocumentUploadFormButtonsOverride } from '@Components/DocumentUploadForm'
import {
    DuplicateDocument,
    PresignedDocumentUpload,
    UnifiedDocument,
    UploadedDocument,
} from '@Entities/document/document-to-upload'
import { hashFile } from '@App/helpers/hash-file'
import { finalizeDocumentUpload } from '@App/api/routes/rightsholder/rightsholder'

export const useDocumentUpload = (
    generateS3PresignedUrl: (
        filename: string,
        fileSize: number,
        description?: Maybe<string>,
        fileHash?: Maybe<string>
    ) => Promise<PresignedDocumentUpload>,
    allowedFileTypes: string[],
    maximumFileSizeInBytes: number,
    buttonOverrideRef: React.ForwardedRef<DocumentUploadFormButtonsOverride>,
    onFileStaged?: (files: UnifiedDocument[]) => void,
    onUpload?: () => void | Promise<void>,
    previouslyUploadedDocs?: UploadedDocument[],
    hashFiles = false
) => {
    const { popupStore, preloaderStore, rightsholderStore } = useContext(StoresContext)
    const [isUploading, setIsUploading] = useState(false)
    const [displayUploadDisclaimer, setDisplayUploadDisclaimer] = useState(false)
    const [filesWereUploaded, setFilesWereUploaded] = useState<boolean>(false)
    const [duplicateDocuments, setDuplicateDocuments] = useState<DuplicateDocument[]>([])
    const [uploadedFiles, setUploadedFiles] = useState<UnifiedDocument[]>(
        previouslyUploadedDocs
            ?.map((doc) => UnifiedDocument.fromApi(doc))
            .sort((a, b) => b.lastModified - a.lastModified) ?? []
    )
    const browseFilePickerRef = useRef<HTMLInputElement>(null)

    const uploadDocuments = async () => {
        setIsUploading(true)
        setDisplayUploadDisclaimer(false)
        preloaderStore.openTransparentPreloader(true)
        const reusedDocuments: DuplicateDocument[] = []
        const uploadPromises = uploadedFiles
            .filter((doc) => !doc.isComplete && doc.hasFile)
            .map(async (docToUpload) => {
                const file = docToUpload.file
                const formData = new FormData()
                formData.append('file', file)
                const fileHash = hashFiles ? await hashFile(file) : undefined
                const uploadResult = await generateS3PresignedUrl(
                    file.name,
                    file.size,
                    docToUpload.description,
                    fileHash
                )
                if (uploadResult.reused) {
                    reusedDocuments.push({
                        name: file.name,
                        originalUploadedAt: uploadResult.originalUploadedAt,
                        originalDocumentStatus: uploadResult.originalDocumentStatus,
                    })
                    return
                }
                await uploadDocumentToPresignedS3(uploadResult.presignedUrl, file, file.type)
                await finalizeDocumentUpload(rightsholderStore.currentRightsholderId, uploadResult.memberUploadId)
            })

        try {
            await Promise.all(uploadPromises)
            setFilesWereUploaded(true)
            setUploadedFiles(uploadedFiles.map((doc) => UnifiedDocument.withComplete(doc)))
            if (reusedDocuments.length > 0) {
                setDuplicateDocuments(reusedDocuments)
            } else if (onUpload) {
                await onUpload()
            }
        } catch {
            popupStore.setAlertData('An error occurred while uploading files', true)
            return
        } finally {
            setIsUploading(false)
            preloaderStore.openTransparentPreloader(false)
        }
    }

    const onDuplicateDocumentsContinue = async () => {
        setDuplicateDocuments([])
        if (onUpload) {
            try {
                await onUpload()
            } catch {
                popupStore.setAlertData('An error occurred while uploading files', true)
            }
        }
    }

    const combineUploadedFiles = (files: FileList, allowMultiple = true) => {
        if (browseFilePickerRef.current) {
            const unifiedDocs = Array.from(files)
                .map((file) => ({
                    file: file,
                    description: undefined,
                }))
                .map((du) => UnifiedDocument.fromUpload(du))

            if (!allowMultiple) {
                if (files.length > 1) {
                    popupStore.setAlertData('Only one file can be uploaded at a time', true)
                    return
                }
                setUploadedFiles([...unifiedDocs])
                return
            }

            const combinedFiles: UnifiedDocument[] = unifiedDocs.concat(uploadedFiles)
            setUploadedFiles([...combinedFiles])
        }
    }

    const onFilesChosen = (files: FileList | null, allowMultiple = true) => {
        if (files) {
            for (const file of files) {
                const fileExtension = file.name.split('.').pop()?.toLowerCase()
                if (!allowedFileTypes.includes(`.${fileExtension}`)) {
                    popupStore.setAlertData('File type not supported', true)
                    return
                }

                if (file.size > maximumFileSizeInBytes) {
                    // Use formatBytes utility for dynamic size string
                    popupStore.setAlertData(`File size should not exceed ${formatBytes(maximumFileSizeInBytes)}`, true)
                    return
                }
            }
            combineUploadedFiles(files, allowMultiple)
        }
    }

    const onRemove = (document: UnifiedDocument) => {
        const modifiedFilesList = uploadedFiles.filter((f) => f !== document)
        setUploadedFiles(modifiedFilesList)
    }

    const addDocuments = (documents: UnifiedDocument[]) => {
        setUploadedFiles((currentFiles) => {
            const nextFiles = [...documents, ...currentFiles]
            return nextFiles.filter(
                (document, index) => nextFiles.findIndex((candidate) => candidate.key === document.key) === index
            )
        })
    }

    const onDescriptionChange = (document: UnifiedDocument, description: string, index: number) => {
        const updatedFiles = [...uploadedFiles]
        updatedFiles[index] = UnifiedDocument.withDescription(document, description)
        setUploadedFiles(updatedFiles)
    }

    const resetForm = () => {
        setUploadedFiles([])
        setFilesWereUploaded(false)
        if (browseFilePickerRef.current) {
            browseFilePickerRef.current.value = ''
        }
    }

    useImperativeHandle(buttonOverrideRef, () => ({
        uploadDocuments: () => uploadDocuments(),
        showUploadDisclaimer: () => setDisplayUploadDisclaimer(true),
        resetForm: () => resetForm(),
    }))

    useEffect(() => {
        onFileStaged && onFileStaged(uploadedFiles)
    }, [uploadedFiles, onFileStaged])

    return {
        uploadedFiles,
        isUploading,
        uploadDocuments,
        resetForm,
        onFilesChosen,
        setDisplayUploadDisclaimer,
        displayUploadDisclaimer,
        filesWereUploaded,
        browseFilePickerRef,
        onRemove,
        onDescriptionChange,
        addDocuments,
        duplicateDocuments,
        onDuplicateDocumentsContinue,
    }
}
